import type { ReactNode } from "react";

import Image from "next/image";

import type { Media } from "@mw/shared/payload-types";
import { Noise } from "@ui/components/core/Noise/Noise";
import { sizes } from "@ui/lib/sizes";
import cloudsCoverImg from "@/app/_blocks/BlockSTTHero/images/clouds-cover.png";
import { SeamlessTexture } from "@ui/components/core/SeamlessTexture/SeamlessTexture";
import { getImageProps, staticImageToMedia } from "@/app/_utilities/payload";

export interface STTHeroPanelProps {
  /** CMS media (url + optional blurDataURL from Payload) — overrides the default static background. */
  backgroundImage?: (Pick<Media, "url"> & Partial<Pick<Media, "blurDataURL" | "width" | "height" | "alt">>) | null;
  children: ReactNode;
}

const panelSizes = sizes("100vw", { lg: "50vw" });

export function STTHeroPanel({ backgroundImage, children }: STTHeroPanelProps) {
  const defaultMedia = staticImageToMedia(cloudsCoverImg);
  const resource = (backgroundImage?.url ? backgroundImage : defaultMedia) as Media;
  const staticBgProps = getImageProps(defaultMedia, { placeholder: true, fill: true });
  const bgImageProps = getImageProps(resource, { placeholder: true, fill: true }) ?? staticBgProps;

  return (
    <div className='tw-relative tw-w-full tw-min-h-[min(700px,calc(100svh-144px))] tw-overflow-hidden'>
      {bgImageProps ? (
        <Image {...bgImageProps} sizes={panelSizes} className='tw-object-cover' priority={false} />
      ) : null}
      <SeamlessTexture className='tw-pointer-events-none' />
      <Noise className='tw-pointer-events-none tw-opacity-60' aria-hidden />

      {/* Content — absolute fill so flex centering matches cover height (equal top/bottom inset) */}
      <div className='tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-10 lg:tw-py-12'>
        {children}
      </div>
    </div>
  );
}

export default STTHeroPanel;
